import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface Payee {
  id: string;
  user_id: string;
  name: string;
  type: 'pf' | 'pj';
  document: string | null;
  nickname: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  is_active: boolean;
  default_category: string | null;
  default_subcategory: string | null;
  created_at: string;
  updated_at: string;
}

export type PayeePayload = Omit<Payee, 'id' | 'user_id' | 'created_at' | 'updated_at'>;

export const usePayees = () => {
  const { user } = useAuth();
  const [payees, setPayees] = useState<Payee[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from('payees')
      .select('*')
      .eq('user_id', user.id)
      .order('name');
    setPayees((data as Payee[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetch(); }, [fetch]);

  const createPayee = async (payload: PayeePayload): Promise<{ success: boolean; data?: Payee; message: string }> => {
    if (!user) return { success: false, message: 'Não autenticado.' };
    const { data, error } = await supabase
      .from('payees')
      .insert({ ...payload, user_id: user.id })
      .select()
      .single();
    if (error) return { success: false, message: error.message };
    await fetch();
    return { success: true, data: data as Payee, message: 'Favorecido criado!' };
  };

  const updatePayee = async (id: string, payload: Partial<PayeePayload>): Promise<{ success: boolean; message: string }> => {
    const { error } = await supabase
      .from('payees')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (error) return { success: false, message: error.message };
    await fetch();
    return { success: true, message: 'Favorecido atualizado!' };
  };

  const deactivatePayee = async (id: string): Promise<{ success: boolean; message: string }> => {
    return updatePayee(id, { is_active: false });
  };

  const reactivatePayee = async (id: string): Promise<{ success: boolean; message: string }> => {
    return updatePayee(id, { is_active: true });
  };

  // Quick create — name required + optional category, used for inline creation in modals
  const quickCreate = async (name: string, category?: string, subcategory?: string): Promise<{ success: boolean; data?: Payee; message: string }> => {
    return createPayee({
      name: name.trim(), type: 'pj', document: null, nickname: null,
      phone: null, email: null, notes: null, is_active: true,
      default_category: category ?? null,
      default_subcategory: subcategory ?? null,
    });
  };

  const activePayees = payees.filter(p => p.is_active);

  /**
   * Returns usage suggestions for a given payee based on transaction history.
   * Finds most-used category, payment_method, account_id, card_id.
   * Also detects if the payee has recurring payment patterns.
   */
  const getPayeeSuggestions = useCallback(async (payeeId: string): Promise<{
    category?: string;
    subcategory?: string;
    payment_method?: 'account' | 'card';
    payment_method_detail?: string;
    account_id?: string;
    card_id?: string;
    suggestsRecurring?: boolean;
  } | null> => {
    if (!user) return null;
    const { data } = await supabase
      .from('transactions')
      .select('category, subcategory, payment_method, payment_method_detail, account_id, card_id, date')
      .eq('user_id', user.id)
      .eq('payee_id', payeeId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(24);
    if (!data || data.length === 0) return null;

    /** Returns most frequent non-null value in an array */
    const freq = <T>(arr: (T | null | undefined)[]): T | undefined => {
      const cleaned = arr.filter((v): v is T => v != null && v !== '');
      if (!cleaned.length) return undefined;
      const counts = cleaned.reduce((acc, v) => {
        const k = String(v);
        acc.set(k, (acc.get(k) ?? 0) + 1);
        return acc;
      }, new Map<string, number>());
      const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
      return cleaned.find(v => String(v) === top?.[0]);
    };

    // Detect recurring: payments in 2+ distinct calendar months
    const months = new Set(data.map(d => d.date?.slice(0, 7)).filter(Boolean));
    const suggestsRecurring = data.length >= 2 && months.size >= 2;

    return {
      category: freq(data.map(d => d.category)),
      subcategory: freq(data.map(d => d.subcategory)),
      payment_method: freq(data.map(d => d.payment_method)) as 'account' | 'card' | undefined,
      payment_method_detail: freq(data.map(d => d.payment_method_detail)),
      account_id: freq(data.map(d => d.account_id)),
      card_id: freq(data.map(d => d.card_id)),
      suggestsRecurring,
    };
  }, [user]);

  /**
   * Returns the IDs of the 5 most recently used payees from transaction history.
   */
  const getRecentPayeeIds = useCallback(async (): Promise<string[]> => {
    if (!user) return [];
    const { data } = await supabase
      .from('transactions')
      .select('payee_id')
      .eq('user_id', user.id)
      .is('deleted_at', null)
      .not('payee_id', 'is', null)
      .order('created_at', { ascending: false })
      .limit(60);
    const seen = new Set<string>();
    const result: string[] = [];
    for (const row of data ?? []) {
      if (row.payee_id && !seen.has(row.payee_id) && result.length < 6) {
        seen.add(row.payee_id);
        result.push(row.payee_id);
      }
    }
    return result;
  }, [user]);

  return {
    payees, activePayees, loading,
    createPayee, updatePayee, deactivatePayee, reactivatePayee,
    quickCreate, getPayeeSuggestions, getRecentPayeeIds,
    refetch: fetch,
  };
};
